import { computed, ref } from 'vue';
import type {
  IHopDongVatTuResponseBase,
  IVatTuHopDongResponseBase,
  IVatTuResponseBase,
} from '@/core/interfaces/ApiResponses';
import type { HopDongHdvtLineDraft } from '@/types/hop-dong';

let draftKeySeq = 0;

function nextDraftKey() {
  draftKeySeq += 1;

  return `hdvt-${Date.now()}-${draftKeySeq}`;
}

export function createEmptyNewHdvtLine(): HopDongHdvtLineDraft {
  return {
    _key: nextDraftKey(),
    kind: 'new',
    ten_vat_tu: '',
    id_danh_muc: [],
    ten_danh_muc: '',
    don_vi_tinh: '',
    so_luong: 1,
    so_tien: 0,
    dac_tinh: '',
  };
}

export function createExistingHdvtLine(input: {
  id_vat_tu: number;
  so_luong: number;
  so_tien: number;
  ma_vat_tu?: string;
  ten_vat_tu?: string;
  don_vi_tinh?: string;
  dac_tinh?: string | null;
}): HopDongHdvtLineDraft {
  return {
    _key: nextDraftKey(),
    kind: 'existing',
    id_vat_tu: input.id_vat_tu,
    ma_vat_tu: input.ma_vat_tu,
    ten_vat_tu: input.ten_vat_tu,
    don_vi_tinh: input.don_vi_tinh,
    so_luong: input.so_luong,
    so_tien: input.so_tien,
    dac_tinh: input.dac_tinh ?? '',
  };
}

export function mapVatTuResponseToDraft(
  vatTu: IVatTuResponseBase & { so_luong?: number; don_gia?: number }
): HopDongHdvtLineDraft {
  return createExistingHdvtLine({
    id_vat_tu: vatTu.id,
    so_luong: vatTu.so_luong ?? 1,
    so_tien: vatTu.don_gia ?? 0,
    ma_vat_tu: vatTu.ma_vat_tu,
    ten_vat_tu: vatTu.ten_vat_tu,
    don_vi_tinh: vatTu.don_vi_tinh,
    dac_tinh: vatTu.dac_tinh,
  });
}

export function mapHopDongVatTuToDraft(
  item: IHopDongVatTuResponseBase
): HopDongHdvtLineDraft {
  return createExistingHdvtLine({
    id_vat_tu: item.id,
    so_luong: item.pivot?.so_luong ?? 0,
    so_tien: item.pivot?.so_tien ?? 0,
    ma_vat_tu: item.ma_vat_tu,
    ten_vat_tu: item.ten_vat_tu,
    don_vi_tinh: item.don_vi_tinh,
    dac_tinh: item.dac_tinh,
  });
}

export function mapVatTuHopDongListToDraft(
  item: IVatTuHopDongResponseBase
): HopDongHdvtLineDraft {
  return createExistingHdvtLine({
    id_vat_tu: item.id_vat_tu || item.id,
    so_luong: item.so_luong_trong_hop_dong || item.so_luong_hop_dong || 0,
    so_tien: item.don_gia ?? 0,
    ma_vat_tu: item.ma_vat_tu,
    ten_vat_tu: item.ten_vat_tu,
    don_vi_tinh: item.don_vi_tinh,
    dac_tinh: item.dac_tinh,
  });
}

export function useHopDongHdvtLines() {
  const lines = ref<HopDongHdvtLineDraft[]>([]);

  const existingIdSet = computed(() => {
    const ids = new Set<number>();

    lines.value.forEach((line) => {
      if (line.kind === 'existing' && line.id_vat_tu) {
        ids.add(line.id_vat_tu);
      }
    });

    return ids;
  });

  const setLines = (next: HopDongHdvtLineDraft[]) => {
    lines.value = next;
  };

  const clearLines = () => {
    lines.value = [];
  };

  const hydrateFromHopDongVatTu = (items: IHopDongVatTuResponseBase[]) => {
    lines.value = items.map(mapHopDongVatTuToDraft);
  };

  const hydrateFromVatTuHopDongList = (items: IVatTuHopDongResponseBase[]) => {
    lines.value = items.map(mapVatTuHopDongListToDraft);
  };

  const addExistingFromVatTu = (
    items: Array<IVatTuResponseBase & { so_luong?: number; don_gia?: number }>
  ) => {
    const next: HopDongHdvtLineDraft[] = [...lines.value];

    items.forEach((item) => {
      if (existingIdSet.value.has(item.id)) {
        const index = next.findIndex(
          (line) => line.kind === 'existing' && line.id_vat_tu === item.id
        );

        if (index >= 0) {
          next[index] = {
            ...next[index],
            so_luong: item.so_luong ?? next[index].so_luong,
            so_tien: item.don_gia ?? next[index].so_tien,
          };
        }

        return;
      }

      next.push(mapVatTuResponseToDraft(item));
    });

    lines.value = next;
  };

  const addNewLine = () => {
    lines.value = [...lines.value, createEmptyNewHdvtLine()];
  };

  const removeLine = (key: string) => {
    lines.value = lines.value.filter((line) => line._key !== key);
  };

  const patchLine = (
    key: string,
    patch: Partial<Omit<HopDongHdvtLineDraft, '_key' | 'kind'>>
  ) => {
    lines.value = lines.value.map((line) =>
      line._key === key ? { ...line, ...patch } : line
    );
  };

  return {
    lines,
    existingIdSet,
    setLines,
    clearLines,
    hydrateFromHopDongVatTu,
    hydrateFromVatTuHopDongList,
    addExistingFromVatTu,
    addNewLine,
    removeLine,
    patchLine,
  };
}
